import type {
  AdminSalesEntriesResponse,
  AdminSalesEntry,
  AdminSalesSummary,
} from '@inflip/contracts/admin';
import { SalesEntryKind } from '@inflip/contracts/admin';
import { addDateDays } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { DataTable } from '../components/data-table';
import {
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { todayDateInput } from '../lib/date-input';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 30;

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const signed = (n: number) => (n < 0 ? `−${won(-n)}` : won(n));

const KIND_META: Record<SalesEntryKind, { label: string; cls: string }> = {
  PAYMENT: { label: '결제 완료', cls: 'text-emerald-600' },
  ADDON: { label: '추가금', cls: 'text-emerald-600' },
  FULL_REFUND: { label: '전액 환불', cls: 'text-rose-600' },
  PARTIAL_REFUND: { label: '부분 환불', cls: 'text-amber-600' },
  ROYALTY: { label: '로열티 입금', cls: 'text-sky-600' },
  ROYALTY_REFUND: { label: '로열티 환급', cls: 'text-rose-600' },
};

/** 기간 바로가기 — 어드민 시간대 오늘 기준 */
function presets(today: string) {
  const month = today.slice(0, 7);
  const firstOfMonth = `${month}-01`;
  const lastMonthEnd = addDateDays(firstOfMonth, -1);
  return [
    { label: '오늘', from: today, to: today },
    { label: '어제', from: addDateDays(today, -1), to: addDateDays(today, -1) },
    { label: '이번 달', from: firstOfMonth, to: today },
    { label: '지난 달', from: `${lastMonthEnd.slice(0, 7)}-01`, to: lastMonthEnd },
    { label: '올해', from: `${today.slice(0, 4)}-01-01`, to: today },
  ];
}

/** 칸(일 YYYY-MM-DD / 월 YYYY-MM)의 기간 */
function bucketRange(key: string): { from: string; to: string } {
  if (key.length === 10) return { from: key, to: key };
  const next = key.endsWith('-12')
    ? `${Number(key.slice(0, 4)) + 1}-01-01`
    : `${key.slice(0, 5)}${String(Number(key.slice(5, 7)) + 1).padStart(2, '0')}-01`;
  return { from: `${key}-01`, to: addDateDays(next, -1) };
}

/**
 * 매출 — 돈이 실제로 움직인 날(원장) 기준으로 결제·추가금·환불(전액·부분)·로열티를 한 줄씩 쌓아 본다.
 * 입금 반환(주문 대금이 아닌 입금)과 판매자 정산·로열티 지급은 매출이 아니라 넣지 않는다.
 * 위는 일·월 합계, 아래는 원장 한 줄씩. 날짜는 어드민 계정 시간대
 */
export function SalesPage() {
  const { session } = useAdminAuth();
  const today = todayDateInput();
  const [dateFrom, setDateFrom] = useListViewState('from', `${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useListViewState('to', today);
  const [groupBy, setGroupBy] = useListViewState<'day' | 'month'>('group', 'day');
  const [kind, setKind] = useListViewState<SalesEntryKind | ''>('kind', '');
  const [page, setPage] = useListViewState('page', 1);
  const [summary, setSummary] = useState<AdminSalesSummary | null>(null);
  const [entries, setEntries] = useState<AdminSalesEntriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setError(null);
    api
      .salesSummary(session.accessToken, { dateFrom, dateTo, groupBy })
      .then((s) => !cancelled && setSummary(s))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [session, dateFrom, dateTo, groupBy]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .salesEntries(session.accessToken, {
        dateFrom,
        dateTo,
        page,
        pageSize: PAGE_SIZE,
        ...(kind ? { kind } : {}),
      })
      .then((e) => !cancelled && setEntries(e))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [session, dateFrom, dateTo, kind, page]);

  function setRange(from: string, to: string) {
    setPage(1);
    setDateFrom(from);
    setDateTo(to);
  }

  // 날짜를 누르면 그 날(월)로 기간을 좁힌다 — 그리기마다 새로 만들어도 가볍다
  const bucketColumns: ColumnDef<AdminSalesSummary['buckets'][number]>[] = [
    {
      id: 'key',
      header: groupBy === 'month' ? '월' : '날짜',
      cell: ({ row }) => (
        <button
          type="button"
          title="이 날짜의 매출만 아래에서 보기"
          className="font-semibold text-brand hover:underline"
          onClick={() => {
            const r = bucketRange(row.original.key);
            setRange(r.from, r.to);
            if (groupBy === 'month') setGroupBy('day');
          }}
        >
          {row.original.key}
        </button>
      ),
    },
    {
      id: 'payment',
      header: '결제',
      meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums' },
      cell: ({ row }) => (
        <>
          <div className="font-semibold">{won(row.original.paymentAmount)}</div>
          <div className="text-xs text-muted-foreground">{row.original.paymentCount}건</div>
        </>
      ),
    },
    {
      id: 'refund',
      header: '환불',
      meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums' },
      cell: ({ row }) => (
        <>
          <div className={row.original.refundAmount ? 'font-semibold text-rose-600' : ''}>
            {row.original.refundAmount ? signed(-row.original.refundAmount) : '—'}
          </div>
          <div className="text-xs text-muted-foreground">
            전액 {row.original.fullRefundCount} · 부분 {row.original.partialRefundCount}
          </div>
        </>
      ),
    },
    {
      id: 'net',
      header: '순매출',
      meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums font-bold' },
      cell: ({ row }) => signed(row.original.netAmount),
    },
    {
      id: 'fee',
      header: '수수료',
      meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums' },
      cell: ({ row }) => signed(row.original.feeAmount),
    },
    {
      id: 'royalty',
      header: '로열티 (순 · 수수료)',
      meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums' },
      cell: ({ row }) =>
        row.original.royaltyCount || row.original.royaltyRefundCount ? (
          <>
            <div className="font-semibold">{signed(row.original.royaltyNetAmount)}</div>
            <div className="text-xs text-muted-foreground">
              수수료 {signed(row.original.royaltyFeeAmount)} · {row.original.royaltyCount}건
            </div>
          </>
        ) : (
          '—'
        ),
    },
  ];

  const entryColumns = useMemo<ColumnDef<AdminSalesEntry>[]>(
    () => [
      {
        id: 'at',
        header: '시각',
        cell: ({ row }) => formatDateTime(row.original.at),
        meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
      },
      {
        id: 'kind',
        header: '종류',
        cell: ({ row }) => (
          <span className={`text-xs font-bold ${KIND_META[row.original.kind].cls}`}>
            {KIND_META[row.original.kind].label}
          </span>
        ),
      },
      {
        id: 'amount',
        header: '금액',
        meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums font-bold' },
        cell: ({ row }) => (
          <span className={row.original.amount < 0 ? 'text-rose-600' : ''}>
            {signed(row.original.amount)}
          </span>
        ),
      },
      {
        id: 'fee',
        header: '수수료',
        meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums text-xs' },
        cell: ({ row }) => signed(row.original.fee),
      },
      {
        id: 'order',
        header: '주문',
        cell: ({ row }) => (
          <>
            <Link
              to="/orders/$orderId"
              params={{ orderId: row.original.orderId }}
              className="font-mono text-xs font-medium text-brand hover:underline"
            >
              {row.original.orderId.slice(0, 8)}
            </Link>
            {row.original.orderGroupId && (
              <div className="font-mono text-[11px] text-muted-foreground">
                묶음 {row.original.orderGroupId.slice(0, 8)}
              </div>
            )}
          </>
        ),
      },
      {
        id: 'listing',
        header: '상품',
        cell: ({ row }) => (
          <span className="block truncate text-sm">{row.original.listingTitle ?? '(삭제됨)'}</span>
        ),
      },
      {
        id: 'parties',
        header: '구매자 → 판매자',
        cell: ({ row }) => (
          <span className="block truncate text-xs text-muted-foreground">
            <Link
              to="/users/$userId"
              params={{ userId: row.original.buyerId }}
              className="text-brand hover:underline"
            >
              {row.original.buyerName ?? '—'}
            </Link>{' '}
            → {row.original.sellerName ?? '—'}
          </span>
        ),
      },
      {
        id: 'memo',
        header: '메모',
        cell: ({ row }) => (
          <span className="block truncate text-xs text-muted-foreground">
            {row.original.memo ?? ''}
          </span>
        ),
      },
    ],
    [],
  );

  if (!session) return null;
  const t = summary?.totals;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="매출"
        description="결제·추가금·환불·로열티를 돈이 움직인 날 기준으로 모아요. 입금 반환과 판매자 지급은 넣지 않아요."
      />

      <div className="mb-5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {presets(today).map((p) => (
          <Chip
            key={p.label}
            active={dateFrom === p.from && dateTo === p.to}
            onClick={() => setRange(p.from, p.to)}
          >
            {p.label}
          </Chip>
        ))}
        <input
          type="date"
          aria-label="시작일"
          className={inputCls}
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => e.target.value && setRange(e.target.value, dateTo)}
        />
        <span>—</span>
        <input
          type="date"
          aria-label="종료일"
          className={inputCls}
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => e.target.value && setRange(dateFrom, e.target.value)}
        />
        <span className="ml-auto flex gap-1.5">
          <Chip active={groupBy === 'day'} onClick={() => setGroupBy('day')}>
            일별
          </Chip>
          <Chip active={groupBy === 'month'} onClick={() => setGroupBy('month')}>
            월별
          </Chip>
        </span>
      </div>

      {error && <ErrorBanner message={error} />}
      {!summary && !error && <LoadingRow />}

      {t && (
        <>
          <h2 className="mb-2 text-sm font-bold">
            주문 매출 · {summary.dateFrom} ~ {summary.dateTo}
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat
              label="결제"
              value={won(t.paymentAmount)}
              sub={`${t.paymentCount}건${t.addonCount ? ` (추가금 ${t.addonCount}건 포함)` : ''}`}
            />
            <Stat
              label="환불"
              value={t.refundAmount ? signed(-t.refundAmount) : '0원'}
              sub={`전액 ${t.fullRefundCount}건 · 부분 ${t.partialRefundCount}건`}
              tone={t.refundAmount ? 'rose' : undefined}
            />
            <Stat label="순매출" value={signed(t.netAmount)} sub="결제 − 환불" strong />
            <Stat
              label="플랫폼 수수료"
              value={signed(t.feeAmount)}
              sub="주문에 고정된 요율, 환불분 차감"
            />
          </div>
          <h2 className="mb-2 mt-6 text-sm font-bold">로열티</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="로열티 입금" value={won(t.royaltyAmount)} sub={`${t.royaltyCount}건`} />
            <Stat
              label="로열티 환급"
              value={t.royaltyRefundAmount ? signed(-t.royaltyRefundAmount) : '0원'}
              sub={`${t.royaltyRefundCount}건 (정정으로 돌려줌)`}
              tone={t.royaltyRefundAmount ? 'rose' : undefined}
            />
            <Stat label="로열티 순" value={signed(t.royaltyNetAmount)} sub="입금 − 환급" strong />
            <Stat
              label="로열티 수수료"
              value={signed(t.royaltyFeeAmount)}
              sub="보고 금액 − 판매자 몫"
            />
          </div>

          <h2 className="mb-2 mt-8 text-sm font-bold">
            {groupBy === 'month' ? '월별' : '일별'} 매출
          </h2>
          {summary.buckets.length === 0 ? (
            <EmptyState message="이 기간에는 매출이 없어요." />
          ) : (
            <DataTable data={summary.buckets} columns={bucketColumns} getRowId={(b) => b.key} />
          )}
        </>
      )}

      <div className="mb-2 mt-8 flex flex-wrap items-center gap-1.5">
        <h2 className="mr-2 text-sm font-bold">
          한 줄씩{entries ? ` · ${entries.total.toLocaleString('ko-KR')}건` : ''}
        </h2>
        <Chip
          active={kind === ''}
          onClick={() => {
            setPage(1);
            setKind('');
          }}
        >
          전체
        </Chip>
        {Object.values(SalesEntryKind).map((k) => (
          <Chip
            key={k}
            active={kind === k}
            onClick={() => {
              setPage(1);
              setKind(k);
            }}
          >
            {KIND_META[k].label}
          </Chip>
        ))}
      </div>
      {entries && entries.items.length === 0 ? (
        <EmptyState message="해당하는 매출이 없어요." />
      ) : (
        entries && <DataTable data={entries.items} columns={entryColumns} getRowId={(e) => e.id} />
      )}
      {entries && entries.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {entries.page} / {entries.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= entries.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </main>
  );
}

function Stat({
  label,
  value,
  sub,
  strong = false,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  strong?: boolean;
  tone?: 'rose';
}) {
  return (
    <div className="rounded-lg bg-muted/60 px-4 py-3.5">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-xl tabular-nums tracking-tight ${strong ? 'font-extrabold' : 'font-bold'} ${tone === 'rose' ? 'text-rose-600' : ''}`}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}
